/**
 * Firebase Auth's error codes, sorted into the handful of things a person can be told.
 *
 * The matrix is deliberately **non-enumerating**: a wrong password, an unknown address and
 * an invalid credential all become the same line, so the page never confirms whether an
 * address has an account. Firebase's email-enumeration protection collapses several of
 * these codes into `invalid-credential` on its own; the sort below assumes it may or may
 * not be on and reads the same either way.
 *
 * The one case that carries data is Google landing on an address that already has a way
 * in: the code names the address, and the sign-in page offers *that* method with the
 * address prefilled — it never links the two silently.
 */

export type AuthProblemKind =
  | 'badCredentials'
  | 'emailInUse'
  | 'weakPassword'
  | 'tooMany'
  | 'network'
  | 'invalidEmail'
  | 'popup'
  | 'methodOff'
  | 'recentLogin'
  | 'accountExists'
  | 'failed';

export interface AuthProblem {
  kind: AuthProblemKind;
  /** `accountExists` only: the address, and the way it already comes in. */
  email?: string;
  method?: 'link' | 'password';
}

/** Thrown by the provider when Google lands on an address that already signs in another way. */
export class AccountExistsError extends Error {
  constructor(
    readonly email: string,
    /** Firebase's method ids — `emailLink`, `password`, `google.com` … — or none if unknown. */
    readonly methods: readonly string[],
  ) {
    super(`auth/account-exists-with-different-credential: ${email}`);
    this.name = 'AccountExistsError';
  }
}

/** The `code` off a FirebaseError, or null for anything else. */
export function authErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

export function classifyAuthError(error: unknown): AuthProblem {
  if (error instanceof AccountExistsError) {
    // Offer the method the address already has. With enumeration protection on the lookup
    // comes back empty, and a password is the honest default: the field is right there and
    // "forgot it?" leads to a reset, whereas a link would arrive unasked-for.
    const method =
      error.methods.includes('emailLink') && !error.methods.includes('password')
        ? 'link'
        : 'password';
    return { kind: 'accountExists', email: error.email, method };
  }

  switch (authErrorCode(error)) {
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/user-disabled':
      return { kind: 'badCredentials' };
    case 'auth/email-already-in-use':
      return { kind: 'emailInUse' };
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return { kind: 'weakPassword' };
    case 'auth/too-many-requests':
      return { kind: 'tooMany' };
    case 'auth/network-request-failed':
      return { kind: 'network' };
    case 'auth/invalid-email':
    case 'auth/missing-email':
      return { kind: 'invalidEmail' };
    case 'auth/popup-blocked':
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return { kind: 'popup' };
    case 'auth/operation-not-allowed':
    case 'auth/admin-restricted-operation':
      return { kind: 'methodOff' };
    case 'auth/requires-recent-login':
      return { kind: 'recentLogin' };
    default:
      return { kind: 'failed' };
  }
}
